import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { GraduationCap, PiggyBank, Target, Plus, BookOpen, Award, Clock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const courses = [
  {
    id: 'management',
    name: 'Management Course',
    description: 'Learn essential management skills and leadership techniques',
    originalPrice: 7500,
    duration: '3 months',
    modules: 12,
    icon: Award
  },
  {
    id: 'hr-diploma',
    name: 'HR Diploma Course',
    description: 'Comprehensive human resources management diploma',
    originalPrice: 5000,
    duration: '6 months',
    modules: 24,
    icon: BookOpen
  },
  {
    id: 'microsoft-advance',
    name: 'Microsoft Advance Course',
    description: 'Advanced Microsoft Office suite and productivity tools',
    originalPrice: 9800,
    duration: '2 months',
    modules: 8,
    icon: GraduationCap
  },
  {
    id: 'people-management',
    name: 'People Management Skills Course',
    description: 'Master the art of managing and motivating teams',
    originalPrice: 6500,
    duration: '4 months',
    modules: 16,
    icon: Clock
  }
];

export const EducationSection = () => {
  const { user, api, refreshUser } = useAuth();
  const [eduSavings, setEduSavings] = useState({ balance: 0, goal: 50000, beneficiary: null });
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    fetchEduSavings();
  }, []);

  const fetchEduSavings = async () => {
    try {
      const res = await api.get('/education/savings');
      setEduSavings(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscountedPrice = (originalPrice) => {
    return (originalPrice * 0.75).toFixed(2); // 25% discount
  };

  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    toast.success(`Selected: ${course.name}. Our team will contact you to complete enrollment.`);
  };

  const progress = eduSavings.goal ? (eduSavings.balance / eduSavings.goal) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="education-section">
      <h1 className="font-syne text-2xl font-bold text-gray-900">Education</h1>

      {/* Savings Overview */}
      <Card className="card-raoul p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">Education Fund</p>
              <p className="text-4xl font-bold text-gray-900">R{eduSavings.balance?.toFixed(2)}</p>
              {eduSavings.beneficiary && (
                <p className="text-gray-500 text-sm mt-1">For: {eduSavings.beneficiary}</p>
              )}
            </div>
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Goal Progress</span>
              <span className="text-gray-900">R{eduSavings.goal?.toLocaleString()}</span>
            </div>
            <Progress value={progress} className="h-2 bg-gray-200" />
            <p className="text-xs text-gray-500">{progress.toFixed(1)}% of goal reached</p>
          </div>

          <AddEducationSavingsDialog onSuccess={() => { fetchEduSavings(); refreshUser(); }} />
        </div>
      </Card>

      {/* Member Discount Banner */}
      <Card className="card-raoul p-4 border-afriperx-gold/20 bg-gradient-to-r from-red-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-afriperx-green/20 flex items-center justify-center">
            <Award className="w-5 h-5 text-afriperx-gold" />
          </div>
          <div>
            <p className="text-gray-900 font-medium">Member Exclusive: 25% Discount on All Courses!</p>
            <p className="text-gray-500 text-sm">As a AfriPerx member, enjoy special pricing on education</p>
          </div>
        </div>
      </Card>

      {/* Available Courses */}
      <div>
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Available Courses</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {courses.map((course) => {
            const Icon = course.icon;
            const discountedPrice = calculateDiscountedPrice(course.originalPrice);
            
            return (
              <Card key={course.id} className="card-raoul p-5 relative overflow-hidden group">
                {/* Discount Badge */}
                <div className="absolute top-4 right-4 bg-afriperx-green text-white text-xs px-2 py-1 rounded-full font-bold">
                  25% OFF
                </div>
                
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-afriperx-gold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-gray-900 font-bold text-lg mb-1">{course.name}</h3>
                    <p className="text-gray-500 text-sm">{course.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-1 text-gray-500">
                    <Clock className="w-4 h-4" />
                    {course.duration}
                  </div>
                  <div className="flex items-center gap-1 text-gray-500">
                    <BookOpen className="w-4 h-4" />
                    {course.modules} modules
                  </div>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-gray-400 text-sm line-through">R{course.originalPrice.toLocaleString()}</p>
                    <p className="text-2xl font-bold text-gray-900">R{parseFloat(discountedPrice).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-600 text-sm font-medium">You Save</p>
                    <p className="text-green-600 font-bold">R{(course.originalPrice * 0.25).toLocaleString()}</p>
                  </div>
                </div>
                
                <Button
                  onClick={() => handleSelectCourse(course)}
                  className="w-full btn-primary"
                  data-testid={`select-course-${course.id}`}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Select Course
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Benefits */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="card-raoul p-5">
          <PiggyBank className="w-8 h-8 text-green-500 mb-3" />
          <h3 className="text-gray-900 font-medium mb-1">Tax Benefits</h3>
          <p className="text-gray-500 text-sm">Education savings may qualify for tax deductions</p>
        </Card>
        <Card className="card-raoul p-5">
          <Target className="w-8 h-8 text-blue-500 mb-3" />
          <h3 className="text-gray-900 font-medium mb-1">Goal-Based</h3>
          <p className="text-gray-500 text-sm">Set and track savings goals for education</p>
        </Card>
        <Card className="card-raoul p-5">
          <GraduationCap className="w-8 h-8 text-purple-500 mb-3" />
          <h3 className="text-gray-900 font-medium mb-1">Flexible Use</h3>
          <p className="text-gray-500 text-sm">Use for tuition, books, or any education expense</p>
        </Card>
      </div>
    </div>
  );
};

const AddEducationSavingsDialog = ({ onSuccess }) => {
  const { api, user } = useAuth();
  const [amount, setAmount] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(amount) > user.main_balance) {
      toast.error('Insufficient balance');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/education/savings?amount=${amount}&beneficiary=${beneficiary || ''}`);
      toast.success('Education savings added');
      setOpen(false);
      setAmount('');
      setBeneficiary('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add savings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="add-edu-savings-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add to Education Fund
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-gray-900 font-syne">Add to Education Savings</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <Label className="text-gray-700">Amount (ZAR)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
              data-testid="edu-amount-input"
            />
            <p className="text-xs text-gray-500 mt-1">Available: R{user?.main_balance?.toFixed(2)}</p>
          </div>
          <div>
            <Label className="text-gray-700">Beneficiary Name (Optional)</Label>
            <Input
              value={beneficiary}
              onChange={(e) => setBeneficiary(e.target.value)}
              placeholder="Child's name"
              className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="confirm-edu-savings-btn">
            {loading ? 'Processing...' : 'Add to Fund'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
